"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import carouselStyles from "./feature-carousel.module.css";

type SlideMetric = {
  label: string;
  value: string;
};

type SlidePanel = {
  label: string;
  title: string;
  copy: string;
};

type Slide = {
  tab: string;
  title: string;
  description: string;
  heading: string;
  badge: string;
  image?: {
    src: string;
    alt: string;
  };
  chartHeights?: number[];
  metrics: SlideMetric[];
  sidePanels: SlidePanel[];
  lineMode?: boolean;
  clusterItems?: string[];
};

const AUTO_ADVANCE_MS = 5500;

type FeatureCarouselProps = {
  slides: Slide[];
};

export function FeatureCarousel({ slides }: FeatureCarouselProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) {
      return;
    }

    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, AUTO_ADVANCE_MS);

    return () => window.clearTimeout(timer);
  }, [activeIndex, slides.length]);

  const activeSlide = slides[activeIndex];
  const selectSlide = (index: number) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }

    const handleTabSelect = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const button = target.closest<HTMLButtonElement>("[data-slide-index]");
      if (!button || !frame.contains(button)) {
        return;
      }

      const index = Number(button.dataset.slideIndex);
      if (Number.isNaN(index)) {
        return;
      }

      setActiveIndex(index);
    };

    frame.addEventListener("mousedown", handleTabSelect);
    frame.addEventListener("touchstart", handleTabSelect, { passive: true });

    return () => {
      frame.removeEventListener("mousedown", handleTabSelect);
      frame.removeEventListener("touchstart", handleTabSelect);
    };
  }, []);

  return (
    <div className={carouselStyles.frame} ref={frameRef}>
      <div className={carouselStyles.stage}>
        <div className={carouselStyles.windowBar} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>

        {activeSlide.image ? (
          <div className={carouselStyles.screenshotSurface} aria-live="polite">
            {slides.map((slide, index) =>
              slide.image ? (
                <Image
                  alt={slide.image.alt}
                  aria-hidden={index === activeIndex ? undefined : true}
                  className={`${carouselStyles.screenshot} ${
                    index === activeIndex
                      ? carouselStyles.screenshotActive
                      : carouselStyles.screenshotHidden
                  }`}
                  fill
                  key={slide.image.src}
                  priority={index === 0}
                  sizes="(max-width: 780px) 92vw, 1180px"
                  src={slide.image.src}
                  unoptimized
                />
              ) : null,
            )}
          </div>
        ) : (
          <div className={carouselStyles.surface} aria-live="polite">
            <div className={carouselStyles.mainPanel}>
              <div className={carouselStyles.surfaceTop}>
                <div>
                  <span className={carouselStyles.metaLabel}>{activeSlide.tab}</span>
                  <h3>{activeSlide.heading}</h3>
                </div>
                <div className={carouselStyles.badge}>{activeSlide.badge}</div>
              </div>

              {activeSlide.chartHeights ? (
                <div className={carouselStyles.chartCard}>
                  <span className={carouselStyles.miniLabel}>Review coverage</span>
                  <div className={carouselStyles.bars} aria-hidden="true">
                    {activeSlide.chartHeights.map((height, index) => (
                      <span key={index} style={{ height: `${height}%` }} />
                    ))}
                  </div>
                  <div className={carouselStyles.chartAxis}>
                    <span>Campaign A</span>
                    <span>Campaign H</span>
                  </div>
                </div>
              ) : activeSlide.lineMode ? (
                <div className={carouselStyles.flowCard}>
                  <span className={carouselStyles.miniLabel}>Structured outputs</span>
                  <div className={carouselStyles.lineStack} aria-hidden="true">
                    <span />
                    <span className={carouselStyles.mid} />
                    <span />
                    <span className={carouselStyles.short} />
                  </div>
                </div>
              ) : (
                <div className={carouselStyles.clusterCard}>
                  <span className={carouselStyles.miniLabel}>Grouped reasons</span>
                  <div className={carouselStyles.clusterGrid}>
                    {activeSlide.clusterItems?.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className={carouselStyles.mainFoot}>
                {activeSlide.metrics.map((metric) => (
                  <div className={carouselStyles.metric} key={metric.label}>
                    <span className={carouselStyles.miniLabel}>{metric.label}</span>
                    <strong>{metric.value}</strong>
                  </div>
                ))}
              </div>
            </div>

            <aside className={carouselStyles.sidePanel}>
              {activeSlide.sidePanels.map((panel) => (
                <div className={carouselStyles.metaCard} key={panel.title}>
                  <span className={carouselStyles.miniLabel}>{panel.label}</span>
                  <strong>{panel.title}</strong>
                  <p>{panel.copy}</p>
                </div>
              ))}
            </aside>
          </div>
        )}
      </div>

      <div className={carouselStyles.tabs}>
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;

          return (
            <button
              key={slide.tab}
              aria-current={isActive ? "true" : undefined}
              className={isActive ? carouselStyles.tabActive : undefined}
              data-slide-index={index}
              onClick={() => selectSlide(index)}
              onMouseDown={() => selectSlide(index)}
              onPointerDown={() => selectSlide(index)}
              onTouchStart={() => selectSlide(index)}
              type="button"
            >
              <strong>{slide.tab}</strong>
              <p>{slide.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
